"use client";

import { useMemo, useState } from "react";
import {
    Alert,
    Group,
    NumberInput,
    Paper,
    Radio,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Textarea,
} from "@mantine/core";
import {
    IconGift,
    IconMail,
    IconSparkles,
    IconHeart,
    IconCheck,
    IconSparkles as IconSecretSparkles,
} from "@tabler/icons-react";
import { AppButton } from "@/src/components/AppButton/AppButton";
import styles from "./page.module.scss";
import { useDictionary } from "@/src/dictionaries/dictionary-provider";

const PRESET_AMOUNTS = [2500, 5000, 7500, 10000, 15000];

export default function GiftCardPage() {
    const [selectedAmount, setSelectedAmount] = useState<string>("5000");
    const [customAmountEUR, setCustomAmountEUR] = useState<number | string>(50);

    const [recipientName, setRecipientName] = useState("");
    const [recipientEmail, setRecipientEmail] = useState("");
    const [senderName, setSenderName] = useState("");
    const [senderEmail, setSenderEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sendToRecipientByEmail, setSendToRecipientByEmail] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const logoUrl =
        "https://ik.imagekit.io/usertwohestia/ateliers-mini-monkeys-transparent.png";

    const amountCents = useMemo(() => {
        if (selectedAmount === "custom") {
            const n = Number(customAmountEUR || 0);
            return Math.max(1000, Math.round(n * 100));
        }
        return Number(selectedAmount || 0);
    }, [selectedAmount, customAmountEUR]);

    const amountLabel = useMemo(() => {
        return new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
        }).format(amountCents / 100);
    }, [amountCents]);

    const fieldErrors = useMemo(() => {
        return {
            amount: amountCents < 1000 ? "Le montant minimum est de 10 €." : "",
            recipientName:
                recipientName.trim().length < 2
                    ? "Veuillez renseigner le nom et prénom du bénéficiaire."
                    : "",
            recipientEmail:
                sendToRecipientByEmail &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())
                    ? "Veuillez renseigner un email bénéficiaire valide."
                    : "",
            senderName:
                senderName.trim().length < 2
                    ? "Veuillez renseigner votre nom et prénom."
                    : "",
            senderEmail:
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderEmail.trim())
                    ? "Veuillez renseigner un email valide."
                    : "",
        };
    }, [
        amountCents,
        recipientName,
        recipientEmail,
        senderName,
        senderEmail,
        sendToRecipientByEmail,
    ]);

    const validationErrors = useMemo(() => {
        return Object.values(fieldErrors).filter(Boolean);
    }, [fieldErrors]);

    const isValid = validationErrors.length === 0;

    const handleSubmit = async () => {
        try {
            setSubmitAttempted(true);
            setError(null);

            if (!isValid) {
                setError("Merci de corriger les champs indiqués avant de continuer.");
                return;
            }

            setLoading(true);

            const res = await fetch("/api/gift-cards/create-checkout-session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    amountCents,
                    currency: "eur",
                    recipientName: recipientName.trim(),
                    recipientEmail: recipientEmail.trim(),
                    senderName: senderName.trim(),
                    senderEmail: senderEmail.trim(),
                    message: message.trim(),
                    sendToRecipientByEmail,
                    logoUrl,
                }),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok || !data?.ok || !data?.url) {
                throw new Error(data?.message || "Impossible de créer le paiement");
            }

            window.location.href = data.url;
        } catch (e: any) {
            setError(e?.message || "Une erreur est survenue");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.hero}>
                <div className={styles.heroBadge}>
                    <IconGift size={16} />
                    <span>Carte cadeau</span>
                </div>

                <h1 className={styles.title}>Offrir une expérience</h1>
                <p className={styles.subtitle}>
                    Offrez une carte cadeau utilisable en ligne pour les activités et les
                    rendez-vous. Vous choisissez ensuite si le bénéficiaire reçoit son
                    code par email ou si vous préférez lui offrir vous-même.
                </p>
            </div>

            <div className={styles.grid}>
                <Paper className={styles.card} withBorder radius="xl" p="xl">
                    <Stack gap="lg">
                        <div>
                            <Text fw={900} size="xl">
                                Personnaliser la carte
                            </Text>
                            <Text c="dimmed" size="sm" mt={4}>
                                Choisissez un montant, renseignez le bénéficiaire et ajoutez un
                                petit mot.
                            </Text>
                        </div>

                        {error ? <Alert color="red">{error}</Alert> : null}

                        {submitAttempted && validationErrors.length > 0 ? (
                            <Alert color="orange" title="Informations à compléter">
                                <Stack gap={4}>
                                    {validationErrors.map((item) => (
                                        <Text key={item} size="sm">
                                            • {item}
                                        </Text>
                                    ))}
                                </Stack>
                            </Alert>
                        ) : null}

                        <div className={styles.banner}>
                            <div className={styles.bannerIcon}>
                                <IconSparkles size={18} stroke={1.8} />
                            </div>
                            <div>
                                <Text className={styles.bannerTitle}>
                                    Une idée cadeau simple et élégante
                                </Text>
                                <Text className={styles.bannerText}>
                                    La carte peut être utilisée directement lors du paiement sur
                                    le site.
                                </Text>
                            </div>
                        </div>

                        <Stack gap="sm">
                            <Text fw={700}>Montant</Text>

                            <Radio.Group
                                value={selectedAmount}
                                onChange={setSelectedAmount}
                                className={styles.amountGroup}
                            >
                                <SimpleGrid cols={{ base: 2, sm: 3 }}>
                                    {PRESET_AMOUNTS.map((v) => (
                                        <Radio.Card
                                            key={v}
                                            value={String(v)}
                                            className={styles.amountCard}
                                        >
                                            <Text fw={800}>
                                                {new Intl.NumberFormat("fr-FR", {
                                                    style: "currency",
                                                    currency: "EUR",
                                                }).format(v / 100)}
                                            </Text>
                                        </Radio.Card>
                                    ))}

                                    <Radio.Card value="custom" className={styles.amountCard}>
                                        <Text fw={800}>Montant libre</Text>
                                    </Radio.Card>
                                </SimpleGrid>
                            </Radio.Group>

                            {selectedAmount === "custom" ? (
                                <NumberInput
                                    label="Montant libre (€)"
                                    min={10}
                                    value={customAmountEUR}
                                    onChange={setCustomAmountEUR}
                                    thousandSeparator=" "
                                    decimalScale={2}
                                    fixedDecimalScale={false}
                                    error={submitAttempted ? fieldErrors.amount : ""}
                                />
                            ) : null}
                        </Stack>

                        <Stack gap="xs">
                            <Text fw={700}>Mode de livraison</Text>

                            <Radio.Group
                                value={sendToRecipientByEmail ? "email" : "manual"}
                                onChange={(value) => setSendToRecipientByEmail(value === "email")}
                            >
                                <Stack gap="sm">
                                    <Radio
                                        value="manual"
                                        label="Je veux offrir la surprise moi-même"
                                    />
                                    <Radio
                                        value="email"
                                        label="Envoyer directement la carte cadeau par email au bénéficiaire"
                                    />
                                </Stack>
                            </Radio.Group>

                            <Text size="sm" c="dimmed">
                                Par défaut, aucun email n’est envoyé au bénéficiaire. Vous
                                recevez toujours la confirmation et le code.
                            </Text>
                        </Stack>

                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                            <TextInput
                                label="Votre nom et prénom"
                                placeholder="Ex. Linda Martin"
                                value={senderName}
                                onChange={(e) => setSenderName(e.currentTarget.value)}
                                error={submitAttempted ? fieldErrors.senderName : ""}
                                required
                            />
                            <TextInput
                                label="Votre email"
                                placeholder="vous@email.com"
                                leftSection={<IconMail size={16} />}
                                value={senderEmail}
                                onChange={(e) => setSenderEmail(e.currentTarget.value)}
                                error={submitAttempted ? fieldErrors.senderEmail : ""}
                                required
                            />
                        </SimpleGrid>

                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                            <TextInput
                                label="Nom et prénom du bénéficiaire"
                                placeholder="Ex. Olivia Martin"
                                value={recipientName}
                                onChange={(e) => setRecipientName(e.currentTarget.value)}
                                error={submitAttempted ? fieldErrors.recipientName : ""}
                                required
                            />

                            <div className={styles.recipientEmailBlock}>
                                <TextInput
                                    label={
                                        sendToRecipientByEmail
                                            ? "Email du bénéficiaire"
                                            : "Email du bénéficiaire (optionnel)"
                                    }
                                    placeholder="beneficiaire@email.com"
                                    leftSection={<IconMail size={16} />}
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.currentTarget.value)}
                                    error={submitAttempted ? fieldErrors.recipientEmail : ""}
                                    required={sendToRecipientByEmail}
                                />

                                {!sendToRecipientByEmail ? (
                                    <div className={styles.secretNote}>
                                        <div className={styles.secretNoteIcon}>
                                            <IconSecretSparkles size={16} stroke={1.9} />
                                        </div>

                                        <div className={styles.secretNoteContent}>
                                            <Text className={styles.secretNoteTitle}>
                                                On garde le secret…
                                            </Text>
                                            <Text className={styles.secretNoteText}>
                                                Aucun email ne sera envoyé au bénéficiaire. Vous
                                                recevrez la carte cadeau pour la remettre vous-même,
                                                au bon moment.
                                            </Text>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </SimpleGrid>

                        <Textarea
                            label="Message personnel"
                            placeholder="Ex. Je te souhaite un merveilleux moment..."
                            minRows={4}
                            value={message}
                            onChange={(e) => setMessage(e.currentTarget.value)}
                        />

                        <AppButton loading={loading} onClick={handleSubmit}>
                            Offrir cette carte ({amountLabel})
                        </AppButton>
                    </Stack>
                </Paper>

                <Paper className={styles.preview} withBorder radius="xl" p="xl">
                    <div className={styles.previewGlowOne} />
                    <div className={styles.previewGlowTwo} />

                    <Stack gap="lg" h="100%" className={styles.previewInner}>
                        <div className={styles.previewRibbon}>
                            <IconGift size={16} />
                            <span>Carte cadeau premium</span>
                        </div>

                        <div className={styles.previewTop}>
                            <div>
                                <Text className={styles.previewEyebrow}>Montant offert</Text>
                                <Text className={styles.previewAmount}>{amountLabel}</Text>
                            </div>

                            <div className={styles.previewSeal}>
                                <IconHeart size={18} />
                            </div>
                        </div>

                        <div className={styles.previewBody}>
                            <div className={styles.previewSection}>
                                <Text className={styles.previewSmall}>Pour</Text>
                                <Text className={styles.previewName}>
                                    {recipientName || "Le bénéficiaire"}
                                </Text>
                            </div>

                            <div className={styles.previewSection}>
                                <Text className={styles.previewSmall}>De la part de</Text>
                                <Text className={styles.previewName}>
                                    {senderName || "Votre nom"}
                                </Text>
                            </div>

                            <div className={styles.previewMessage}>
                                <Text className={styles.previewMessageLabel}>Message</Text>
                                <Text size="sm" className={styles.previewMessageText}>
                                    {message ||
                                        "Votre message apparaîtra ici dans la carte cadeau."}
                                </Text>
                            </div>
                        </div>

                        <div className={styles.previewFeatures}>
                            <div className={styles.previewFeature}>
                                <IconCheck size={16} />
                                <span>
                                    {sendToRecipientByEmail
                                        ? "Envoi email au bénéficiaire"
                                        : "Surprise à remettre vous-même"}
                                </span>
                            </div>
                            <div className={styles.previewFeature}>
                                <IconCheck size={16} />
                                <span>Utilisable en ligne</span>
                            </div>
                        </div>

                        <div className={styles.previewFooter}>
                            <Text size="sm">
                                Une attention délicate, élégante et facile à offrir.
                            </Text>
                        </div>
                    </Stack>
                </Paper>
            </div>
        </div>
    );
}